import { G } from '@mobily/ts-belt';

import { buildCampaignPlanOptions, CCB_PLAN_NAME } from '@Common/bo/b2b_campaign_plan_options';
import { B2bSavingPlanDeductibilityName, isFullyManagedPlan } from '@Common/types/b2b/plans';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import { B2bCampaignType } from '@Common/types/bo/b2b/campaigns';
import { B2bCompanyPlan, B2bCompanyPlanTypeParameters } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';

import { AllocationsFileError } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/allocationsFileError';

/**
 * Returns the plans that an allocation file can use in its "Dispositif*" column.
 * The names are the same as in the file template and in the review step plan select.
 */
export function keyAllocationFilePlanOptions({
  plans,
  campaignType,
  matchingContributionRelatedCashSource,
}: {
  plans: B2bCompanyPlan[];
  campaignType: B2bCampaignType;
  matchingContributionRelatedCashSource: B2bCashSource | null | undefined;
}) {
  return buildCampaignPlanOptions({
    products: plans,
    campaignType,
    matchingContributionRelatedCashSource,
  }).map(({ name, product, sharesPlan, deductibility }) => ({
    name,
    deductibility,
    plan: {
      ...sharesPlan,
      instruments_cash_sources_libre: product.instruments_cash_sources_libre,
      is_cash_sources_per_instrument:
        !isFullyManagedPlan(sharesPlan.type) && product.is_cash_sources_per_instrument,
    },
  }));
}

type AllocationFilePlanOption = ReturnType<typeof keyAllocationFilePlanOptions>[number];

/**
 * The name that the file template used before it took the names of the plan select.
 * An allocation file made from an old template can contain it.
 */
function legacyPlanName({ plan, deductibility }: AllocationFilePlanOption): string {
  const deductibilityName = deductibility
    ? ` ${B2bSavingPlanDeductibilityName[deductibility]}`
    : '';
  const labelName = plan.plan_label ? ` (${plan.plan_label})` : '';
  return `${B2bCompanyPlanTypeParameters[plan.type].name}${deductibilityName}${labelName}`;
}

function planNotFoundError(planName: string) {
  return new AllocationsFileError(
    `${planName} not found in chosen contracts`,
    'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_plan',
    { plan: planName },
  );
}

/** Finds the plan of a "Dispositif*" cell. A CCB row uses the plan of its CCB instrument. */
export function findAllocationFilePlanOption({
  planOptions,
  planName,
  instrument,
}: {
  planOptions: AllocationFilePlanOption[];
  planName: string;
  instrument: GetInstrumentsResponseItem | undefined;
}): AllocationFilePlanOption {
  if (planName === CCB_PLAN_NAME) {
    const ccbOption = planOptions.find(
      (option) =>
        G.isNotNullable(instrument) &&
        !isFullyManagedPlan(option.plan.type) &&
        option.plan.instruments_universe.includes(instrument.id),
    );
    if (G.isNullable(ccbOption)) {
      throw planNotFoundError(planName);
    }
    return ccbOption;
  }

  const option = planOptions.find((entry) => entry.name === planName);
  if (G.isNotNullable(option)) {
    return option;
  }

  const legacyOptions = planOptions.filter((entry) => legacyPlanName(entry) === planName);
  if (legacyOptions.length > 1) {
    throw new AllocationsFileError(
      `${planName} matches several plans in chosen contracts`,
      'resources.bo_common.b2b.campaigns.sections.allocations.errors.ambiguous_plan',
      { plan: planName },
    );
  }
  const [legacyOption] = legacyOptions;
  if (G.isNullable(legacyOption)) {
    throw planNotFoundError(planName);
  }
  return legacyOption;
}
